export type CreditCardPurchaseIntent = {
  idempotencyKey: string;
  fingerprint: string;
};

export type CardIntentStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function stableJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  return `{${Object.entries(value).filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(',')}}`;
}

async function fingerprint(value: unknown) {
  const bytes = new TextEncoder().encode(stableJson(value));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

/** One durable intent per explicit caller scope; contains no financial payload. */
export class CreditCardPurchaseIntentController<T> {
  private inFlight: { serialized: string; promise: Promise<T> } | null = null;
  private confirmedKey: string | null = null;
  private firstAttempt = false;

  constructor(
    private readonly scope: string,
    private readonly storage: CardIntentStorage,
  ) {}

  private storageKey() { return `metajuntos:card-purchase-intent:v1:${this.scope}`; }

  private async resolve(semanticPayload: unknown): Promise<CreditCardPurchaseIntent> {
    const hash = await fingerprint(semanticPayload);
    const stored = this.storage.getItem(this.storageKey());
    if (stored !== null) {
      this.firstAttempt = false;
      let value: CreditCardPurchaseIntent;
      try { value = JSON.parse(stored) as CreditCardPurchaseIntent; }
      catch { throw new Error('card_purchase_intent_storage_invalid'); }
      if (!value || typeof value.idempotencyKey !== 'string' ||
        !/^client:card-purchase:[a-f0-9-]{36}$/.test(value.idempotencyKey) ||
        !/^[a-f0-9]{64}$/.test(value.fingerprint)) {
        throw new Error('card_purchase_intent_storage_invalid');
      }
      if (value.fingerprint !== hash) throw new Error('card_purchase_idempotency_payload_conflict');
      return value;
    }
    const intent = { idempotencyKey: `client:card-purchase:${crypto.randomUUID()}`, fingerprint: hash };
    // Persist before transport. Storage failure must prevent the write, not
    // silently downgrade an active intention to an ephemeral random key.
    this.storage.setItem(this.storageKey(), JSON.stringify(intent));
    this.firstAttempt = true;
    return intent;
  }

  run(semanticPayload: unknown, operation: (intent: CreditCardPurchaseIntent) => Promise<T>): Promise<T> {
    const serialized = stableJson(semanticPayload);
    if (this.inFlight) {
      return this.inFlight.serialized === serialized
        ? this.inFlight.promise
        : Promise.reject(new Error('card_purchase_idempotency_payload_conflict'));
    }
    // Capture immutable semantic input before yielding to asynchronous hashing.
    const request = this.resolve(JSON.parse(serialized)).then(async intent => {
      const result = await operation(intent);
      this.confirmedKey = intent.idempotencyKey;
      return result;
    });
    this.inFlight = { serialized, promise: request };
    const release = () => { if (this.inFlight?.promise === request) this.inFlight = null; };
    void request.then(release, release);
    return request;
  }

  complete() {
    if (this.inFlight || !this.confirmedKey) return false;
    const stored = this.storage.getItem(this.storageKey());
    if (!stored || (JSON.parse(stored) as CreditCardPurchaseIntent).idempotencyKey !== this.confirmedKey) return false;
    this.storage.removeItem(this.storageKey());
    this.confirmedKey = null;
    return true;
  }

  resolveFailure(definitiveRollback: boolean) {
    if (this.inFlight) return false;
    const canDiscard = definitiveRollback && this.firstAttempt && !this.confirmedKey;
    this.firstAttempt = false;
    if (!canDiscard) return false;
    this.storage.removeItem(this.storageKey());
    return true;
  }

  // A later rejection cannot disprove an earlier ambiguous commit. Only the
  // first attempt with an explicit SQL rollback can release a rejected intent.
}
